import { ApolloClient, createHttpLink, DocumentNode, gql, InMemoryCache, useQuery } from '@apollo/client';
import type { GridAggregationFunction, GridColDef, GridPivotModel, GridSortModel } from '@mui/x-data-grid-premium';
import { Filter, SearchState } from '@searchkit/client';
import { filter, find, uniq } from 'lodash';
import { useMemo } from 'react';
import { Result } from 'sr-types/lib/search/v1/graphql';
import { authLink, UserProfile } from '../../common/auth/api';
import { QuickFilter } from '../../common/components/filtering/QuickFilterItem';
import { FETCH_POLICY_CONFIG } from '../../common/constants/constants';
import { FEATURE_CONSTANTS } from '../../common/constants/featureConstants';
import { searchClient, slimResultsQuery } from '../../common/list/slimQuery';

export interface FilterObj {
    valueFilter: {};
    dateFilter: any[];
    rangeFilter: any[];
    booleanFilter: any[];
    singleSelectFilter: any[];
}

export interface ChartFilter {
    displayType: string;
    label: string;
    name: string;
    value: string;
    facet?: string;
    hide: boolean;
    default: string;
    quickFilter?: boolean;
    preFilter?: boolean;
    required: boolean;
    additionalCriteria: Criterion[];
}

export interface Criterion {
    identifier: string;
    value: string;
}

export interface FacetData {
    results: Result;
}

export interface Field {
    field: string;
    label: string;
    type: string;
    width?: number;
    sortable?: boolean;
    valueGetter?: string;
    format?: string;
    link?: string;
    formula?: string;
    groupingValueGetter?: GroupingValueGetterDefinition | null;
}

export interface GroupingValueGetterDefinition {
    name: string;
    delimiter?: string;
    trim?: boolean;
    fallback?: string;
}

export interface GroupingColumnDefinition {
    headerName?: string;
    width?: number;
    minWidth?: number;
    flex?: number;
    align?: 'left' | 'center' | 'right';
    headerAlign?: 'left' | 'center' | 'right';
    sortable?: boolean;
    leafField?: string;
    mainGroupingCriteria?: string;
    hideDescendantCount?: boolean;
    renderCell?: string;
    valueFormatter?: string;
}

export interface ReportAggregate {
    key: string;
    value: string;
}

export interface ReportColumnVisibility {
    key: string;
    value: boolean;
}

export interface ReportSpec {
    fields: Field[];
    sortBy: GridSortModel;
    groupBy: string[];
    aggregates: ReportAggregate[];
    filters: ChartFilter[];
    columnVisibilities: ReportColumnVisibility[];
    pinnedColumns?: { left: string[]; right: string[] } | null;
    pivotModel?: GridPivotModel | null;
    groupingColDef?: GroupingColumnDefinition | null;
    rowGroupingColumnMode?: 'single' | 'multiple' | null;
}

export interface ReportDefinition {
    id: string;
    name: string;
    baseEntity?: string;
    description: string;
    type: string;
    listed: boolean;
    reportSpec: ReportSpec;
}

export const MongoReportType = 'MongoChart';
export const MUIReportType = 'MUI';
export const ElasticReport = 'ElasticChart';

export const managePivotToken = 'report.manage.pivot.tooltip';
export const refreshLabelToken = 'refresh.data.label';
export const saveImageLabelToken = 'save.image.data.label';
export const exportDataLabelToken = 'report.export.data.label';
export const exportLabelToken = 'report.export.label';
export const sheetExportInformationToken = 'report.export.information';
export const sheetDashboardNameToken = 'report.dashboard.name';
export const sheetExportedOnToken = 'report.exported.on';
export const sheetExportedByToken = 'report.exported.by';
export const exportExcelDataToken = 'report.export.excel.data';
export const exportImageDataToken = 'report.export.image.data';
export const reportingCurrencyFilterNameToken = 'Reporting Currency';
export const EXCEL_BLOB_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
export const CSV_BLOB_TYPE = 'text/csv;charset=utf-8;';

export const initialFilter = (
    activeOrganizationAccount: string,
    ownerOrganizationIds: string[] = [],
    isMUIReport?: boolean,
    bookingId?: string
) => {
    const initialFilter = isMUIReport ? {} : { ownerId: activeOrganizationAccount };

    if (bookingId) {
        initialFilter['booking.id'] = bookingId;
    }

    if (ownerOrganizationIds.length > 0) {
        initialFilter['ownerOrganizationId'] = { $in: ownerOrganizationIds };
    }

    return initialFilter;
};

export const isGroupDataSecurityEnabled = (activeOrganizationAccount: string, userProfile: UserProfile) => {
    if (userProfile && userProfile.memberships) {
        // filter the active organization membership.
        const activeMemberships = filter(userProfile.memberships, (val) => {
            return val.id === activeOrganizationAccount;
        });

        if (activeMemberships.length > 0) {
            //check for the Group Data Security feature in active organization membership.
            const groupSecurityFeature = find(activeMemberships[0].access, (val) => {
                return val.feature === FEATURE_CONSTANTS.groupDataSecurity;
            });

            return !!groupSecurityFeature;
        }
    }

    return false;
};

export const client = new ApolloClient({
    link: authLink.concat(
        createHttpLink({
            uri: '/reporting/v1/'
        })
    ),
    cache: new InMemoryCache({
        addTypename: false
    })
});

type FragmentColumn = {
    label: string;
    dataType: string;
};

type FragmentRecord = {
    data: any[];
};

export type ReportingFragment = {
    id: string;
    name: string;
    columns: FragmentColumn[];
    records: FragmentRecord[];
};

export const reportListQuery: DocumentNode = gql`
    query ReportsList($app: String!) {
        reportsList(app: $app) {
            id
            name
            type
            description
        }
    }
`;

export const reportDetailQuery: DocumentNode = gql`
    query Report($id: String!) {
        report(id: $id) {
            fragments {
                id
                name
                columns {
                    id
                    label
                    dataType
                }
                records {
                    data
                }
            }
        }
    }
`;

export const mongoDbReportDetailQuery: DocumentNode = gql`
    query ReportDefinition($id: String!) {
        reportDefinition(id: $id) {
            id
            name
            baseEntity
            mongoDashboardId
            filters {
                name
                value
                label
                displayType
                hide
                default
                quickFilter
                required
                additionalCriteria {
                    identifier
                    value
                }
            }
            fragments {
                id
                name
                mongoChartId
                filters {
                    name
                    value
                    label
                    displayType
                    hide
                    default
                    quickFilter
                    required
                    additionalCriteria {
                        identifier
                        value
                    }
                }
            }
        }
    }
`;

export const MUIReportDetailQuery: DocumentNode = gql`
    query ReportDefinition($id: String!) {
        reportDefinition(id: $id) {
            id
            name
            baseEntity
            description
            type
            reportSpec {
                groupBy
                rowGroupingColumnMode
                groupingColDef {
                    headerName
                    width
                    minWidth
                    flex
                    align
                    headerAlign
                    sortable
                    leafField
                    mainGroupingCriteria
                    hideDescendantCount
                    renderCell
                    valueFormatter
                }
                fields {
                    field
                    label
                    type
                    width
                    sortable
                    valueGetter
                    format
                    link
                    formula
                    groupingValueGetter {
                        name
                        delimiter
                        trim
                        fallback
                    }
                }
                sortBy {
                    field
                    sort
                }
                aggregates {
                    key
                    value
                }
                pinnedColumns {
                    left
                    right
                }
                filters {
                    name
                    value
                    facet
                    label
                    displayType
                    default
                    hide
                    quickFilter
                    required
                    preFilter
                    additionalCriteria {
                        identifier
                        value
                    }
                }
                columnVisibilities {
                    key
                    value
                }
                pivotModel {
                    rows {
                        field
                        sort
                        hidden
                    }
                    columns {
                        field
                        sort
                        hidden
                    }
                    values {
                        field
                        aggFunc
                        hidden
                    }
                }
            }
        }
    }
`;

export const widgetsList: DocumentNode = gql`
    query WidgetsList($app: String!) {
        widgetsList(app: $app) {
            id
            name
            baseEntity
            mongoDashboardId
            type
            filters {
                name
                value
                label
                displayType
                hide
                default
            }
            fragments {
                id
                name
                mongoChartId
                showTitle
                colSize
                filters {
                    name
                    value
                    label
                    displayType
                    hide
                    default
                }
            }
        }
    }
`;

export const getInitialfacet = (): FacetData => {
    const facetData = {
        results: {
            facets: [],
            hits: {
                items: [],
                page: {
                    total: 0,
                    totalPages: 1,
                    pageNumber: 0,
                    from: 0,
                    size: 5000
                }
            }
        }
    };

    return { ...facetData };
};

export const defaultFilterCreation = (id: string, variables: SearchState, filters: any[], chartFilter: any) => {
    variables.filters = [];

    let defaultFilters = filters.filter((filterVal: ChartFilter) => {
        return filterVal.default !== '';
    });

    defaultFilters.forEach((filterVal: ChartFilter) => {
        const identifier = filterVal.name;
        const displayType = filterVal.displayType;

        switch (displayType) {
            case 'CustomListFacet':
            case 'CustomComboBoxFacet':
                let val: string[] = JSON.parse(filterVal.default);
                val.forEach((fval) => {
                    variables.filters.push({
                        identifier,
                        value: fval
                    });
                });
                chartFilter[identifier] = { $in: val };
                break;
            case 'SingleSelectFacet':
                const singleVal: string = filterVal.default;
                variables.filters.push({
                    identifier,
                    value: singleVal
                });
                chartFilter[identifier] = { $eq: `${singleVal}` };
                break;
            case 'BooleanFacet':
                let defaulVal = filterVal.default === 'true';
                chartFilter[identifier] = defaulVal;
                variables.filters.push({
                    identifier,
                    value: filterVal.default
                });
                break;
            case 'DateRangeFacet':
                let dateval: { dateMax: string; dateMin: string } = JSON.parse(filterVal.default);
                variables.filters.push({
                    identifier,
                    dateMin: dateval.dateMin,
                    dateMax: dateval.dateMax
                });
                chartFilter[identifier] = {
                    $gte: new Date(dateval.dateMin),
                    $lte: new Date(dateval.dateMax)
                };
                break;
            case 'RangeSliderFacet':
                let rangeVal: { max: number; min: number } = JSON.parse(filterVal.default);
                variables.filters.push({
                    identifier,
                    min: rangeVal.min,
                    max: rangeVal.max
                });
                chartFilter[identifier] = {
                    $gte: rangeVal.max,
                    $lte: rangeVal.min
                };
        }
    });

    localStorage.setItem(`filters_${id}`, JSON.stringify(variables));
};

export const setInitialFilter = (variables: SearchState, chartFilter: any, singleSelectFilter: string[] = []) => {
    let variablesFilter = [...variables.filters];
    const filterObj = createFilterObject(variablesFilter, singleSelectFilter);
    handleChangeFilter(filterObj, chartFilter);
};

export const createFilterObject = (filterArr: Filter[], singleSelectFilters: string[] = []): FilterObj => {
    const booleanFilter = [];
    const valueFilter = [];
    const singleSelectFilter = [];
    const dateFilter = [];
    const rangeFilter = [];
    filterArr.map((val) => {
        if (val.value) {
            if (val.value === 'true' || val.value === 'false') {
                booleanFilter.push(val);
            } else if (singleSelectFilters.includes(val.identifier)) {
                singleSelectFilter.push(val);
            } else {
                valueFilter.push(val);
            }
        }
        if (val.dateMin && val.dateMax) {
            dateFilter.push(val);
        }
        if (val.max && val.min) {
            rangeFilter.push(val);
        }
    });
    const newvalue = {};
    valueFilter.forEach((val) => {
        if (newvalue[val.identifier]) newvalue[val.identifier] = [...newvalue[val.identifier], val.value];
        else newvalue[val.identifier] = [val.value];
    });

    return {
        valueFilter: newvalue,
        dateFilter,
        rangeFilter,
        booleanFilter,
        singleSelectFilter
    };
};

export const handleChangeFilter = (filterObj: FilterObj, filter: any) => {
    if (filterObj.booleanFilter) {
        filterObj.booleanFilter.forEach((val) => {
            let boolVal = val.value === 'true';
            filter[val.identifier] = boolVal;
        });
    }
    if (filterObj.dateFilter && filterObj.dateFilter.length > 0) {
        // Checking for date range filter
        filterObj.dateFilter.forEach((val: Filter) => {
            filter[val.identifier] = {
                $gte: new Date(val.dateMin),
                $lte: new Date(val.dateMax)
            };
        });
    }
    //checking for range filter without date.
    if (filterObj.rangeFilter && filterObj.rangeFilter.length > 0) {
        filterObj.rangeFilter.forEach((val: Filter) => {
            filter[val.identifier] = {
                $gte: val.max,
                $lte: val.min
            };
        });
    }
    //checking for value match filter
    if (filterObj.valueFilter) {
        const filterVal = filterObj.valueFilter;
        for (let key in filterVal) {
            filter[key] = { $in: filterVal[key] };
        }
    }

    //checking for single select filter
    if (filterObj.singleSelectFilter) {
        filterObj.singleSelectFilter.forEach((val: Filter) => {
            filter[val.identifier] = { $eq: val.value };
        });
    }
    return filter;
};

export const useOwnerOrganization = (entity: string, activeOrganizationAccount: string) => {
    const { loading, error, data } = useQuery(slimResultsQuery(), {
        client: searchClient,
        variables: {
            filters: [{ identifier: 'entity', value: entity }],
            page: {
                from: 0,
                size: 1
            }
        },
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        notifyOnNetworkStatusChange: false
    });
    const ownerOrgFacet = useMemo(() => {
        if (data && data.results) {
            const owner = data.results.facets.find((val) => val.identifier === 'ownerOrganizationName');
            return owner;
        }
    }, [data]);

    return { loading, error, ownerOrgFacet };
};

export const createColumns = (chartName: string) => {
    const chart = chartName.split('_')[1];

    switch (chart) {
        case 'BookingByStatus': {
            return [
                { field: 'status', headerName: 'Booking Status', flex: 1, width: 100 },
                { field: 'booking', headerName: 'Booking', flex: 1, width: 100 }
            ];
        }
        case 'BookingsByMonth': {
            return [
                { field: 'month', headerName: 'Booking Month', flex: 1, width: 100 },
                { field: 'booking', headerName: 'Booking', flex: 1, width: 100 }
            ];
        }
        case 'BookingsByType': {
            return [
                { field: 'booking', headerName: 'Booking', flex: 1, width: 100 },
                { field: 'type', headerName: 'Booking Type', flex: 1, width: 100 }
            ];
        }
        case 'UniqueGuestsByBookingType': {
            return [
                { field: 'type', headerName: 'Booking Type', flex: 1, width: 100 },
                { field: 'guest', headerName: 'Guest', flex: 1, width: 100 }
            ];
        }
        case 'UniqueGuests': {
            return [{ field: 'guest', headerName: 'Unique Guest', flex: 1, width: 100 }];
        }
        case 'UniqueOrganizations': {
            return [{ field: 'guest', headerName: 'Unique Organizations', flex: 1, width: 100 }];
        }
        case 'UniqueDepartments': {
            return [{ field: 'guest', headerName: 'Unique Departments', flex: 1, width: 100 }];
        }
        case 'VisitorsByMonth': {
            return [
                { field: 'month', headerName: 'Month', flex: 1, width: 100 },
                { field: 'guest', headerName: 'Guest', flex: 1, width: 100 }
            ];
        }
    }
};

export const createRows = (mrows: any, columns: GridColDef[]) => {
    const rows = [];
    mrows.forEach((row: any, externalIndex) => {
        let val = {};
        val['id'] = externalIndex;
        const record = uniq(Object.values(row));
        record.forEach((data, index) => {
            const { field } = columns[index];
            val[field] = data;
        });
        rows.push(val);
    });

    return rows;
};

export const getSingleSelectFilters = (filters) => {
    const singleFilters = [];
    filters.forEach((val: ChartFilter) => {
        if (val.displayType === 'SingleSelectFacet') {
            singleFilters.push(val.name);
        }
    });
    return singleFilters;
};

export const getMongoChartMaxDataAge = () => {
    const MONGO_CHART_MAX_DATA_AGE = 3600;
    return MONGO_CHART_MAX_DATA_AGE;
};

export const createQuickFilter = (quickFilters: any[], variablesFilter: Filter[]) => {
    quickFilters.forEach((qf: QuickFilter) => {
        const vf = variablesFilter.filter((f) => f.identifier === qf.id).map((v) => v.value);
        qf.values = vf;
        if (qf.displayType === 'SingleSelectFacet') {
            const selectedValue = qf.values[0];
            qf.options = [...qf.originalOptions].filter((o) => o !== selectedValue);
        }
    });
};

type ChartCell = string | number | boolean | null;

type ChartData<K extends string = string, V = ChartCell> = {
    fields: Record<K, string>; // key -> display label
    documents: Array<Partial<Record<K, V>>>;
};

type FormattedRow<V> = Array<V | ''>;

export const formatChartData = <K extends string, V = ChartCell>(chartData: ChartData<K, V>): FormattedRow<V>[] => {
    const columnKeys = Object.keys(chartData.fields) as K[];

    return chartData.documents.map((document) =>
        columnKeys.map((field) => (document[field] === undefined ? '' : document[field]!))
    );
};

export const downloadFile = (blobData: Blob, downloadName: string) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blobData);
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};

export const utilization: GridAggregationFunction<{ availableDays: number; bookedDays1: number }, number> = {
    label: 'custom',
    getCellValue: ({ row }) => ({ availableDays: row.availableDays, bookedDays1: row.bookedDays1 }),
    apply: ({ values }) => {
        let availableDays = 0;
        let bookedDays1 = 0;
        values.forEach((value) => {
            if (value) {
                availableDays += value.availableDays;
                bookedDays1 += value.bookedDays1;
            }
        });
        return bookedDays1 / availableDays;
    },
    columnTypes: ['number']
};

export const splitPattern = (pattern: string) => {
    const integerPart = pattern.split('.')[0];
    const leadingZeros = (integerPart.match(/0/g) || []).length; // Count leading zeros
    return [integerPart.replace(/[^0]/g, ''), leadingZeros];
};

export const formatInteger = (integerPart: any, pattern: string) => {
    // Add commas to the integer part
    const [integerDigits, leadingZeros] = splitPattern(pattern);
    let formattedInteger = integerPart;

    // Format leading zeros
    formattedInteger = formattedInteger.padStart(leadingZeros, '0');

    // Add commas based on the grouping size defined in the pattern
    const groupSize = pattern.match(/(,)/g)?.length || 0;
    let regex = new RegExp(`\\B(?=(\\d{3})+(?!\\d))`, 'g'); // Match place for commas
    formattedInteger = formattedInteger.replace(regex, ',');

    // Adjust to ensure correct leading zeros if pattern has fewer digits
    if (formattedInteger.length > integerDigits) {
        formattedInteger = formattedInteger.slice(-integerDigits);
    }

    return formattedInteger;
};

export const formatDecimal = (decimalPart: string, maxDecimalPlaces: number) => {
    const paddedDecimal = decimalPart.padEnd(maxDecimalPlaces, '0'); // Pad with zeroes if needed
    return paddedDecimal.slice(0, maxDecimalPlaces); // Truncate to max decimal places if needed
};

export const formatNumber = (num: any, pattern: string) => {
    // Determine if percent symbol is needed
    const isPercentage = pattern.includes('%');
    const formattingPattern = pattern.replace(/[%#]/g, '');

    // Adjust the number for percentage if needed
    if (isPercentage) {
        num *= 100; // Multiply by 100 to convert to percentage
    }

    // Split the pattern into integer and decimal sections
    const [integerPattern, decimalPattern] = formattingPattern.split('.');

    // Handle decimal part
    const decimalPlaces = decimalPattern ? decimalPattern.length : 0;
    const [integerPart, decimalPart] = String(Math.abs(num)).split('.');

    // Format integer part
    const formattedInteger = formatInteger(integerPart, integerPattern);

    // Format decimal part, if there's a decimal pattern specified
    let formattedDecimal = '';
    if (decimalPlaces > 0) {
        formattedDecimal = formatDecimal(decimalPart || '', decimalPlaces);
    }

    // Combine formatted integer and decimal parts
    let result = formattedInteger;
    if (formattedDecimal) {
        result += '.' + formattedDecimal;
    }

    // Add percentage symbol if needed
    if (isPercentage) {
        result += '%';
    }

    // Handle negative numbers
    if (num < 0) {
        result = '-' + result;
    }

    return result;
};
